import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { cn } from '../../utils';

interface SortableRowProps {
  id: string;
  children: ReactNode;
  /** Extra classes for the <tr>. */
  className?: string;
  /** Padding class for the handle cell — should match the density pad of its siblings. */
  cellPadding?: string;
  /** aria-label for the handle button. */
  handleLabel?: string;
}

/**
 * A <tr> wrapped with @dnd-kit/sortable.
 * Renders a GripVertical handle as the first <td>.
 * When drag is not needed, DataTable renders a plain <tr> instead.
 */
export function SortableRow({
  id,
  children,
  className,
  cellPadding = 'h-10 px-2',
  handleLabel = 'Drag to reorder',
}: SortableRowProps) {
  const uid = useId();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.08)' : undefined,
    position: isDragging ? ('relative' as const) : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(className, isDragging && 'opacity-80')}
      aria-describedby={uid}
    >
      <td
        className={cn(cellPadding, 'align-middle w-8')}
        style={isDragging ? { borderTop: '2px solid var(--color-accent)' } : undefined}
      >
        {/* aria-live region for keyboard drag announcements */}
        <span id={uid} className="sr-only" aria-live="assertive" />
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={handleLabel}
          className={cn(
            'inline-flex size-4 cursor-grab items-center justify-center',
            'text-[var(--color-text-tertiary)] transition-colors',
            'hover:text-[var(--color-text-secondary)]',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]',
            'active:cursor-grabbing',
          )}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={14} aria-hidden />
        </button>
      </td>
      {children}
    </tr>
  );
}
